import LoadingIndicator from 'components/atoms/loading-indicator'
import Text from 'components/atoms/text'
import SearchResultsOverview from 'components/layouts/search-results-overview'
import Filters from 'components/molecules/filters'
import Overlay from 'components/molecules/overlay'
import StockHeader from 'components/organisms/stock-header'
import StockItems from 'components/organisms/stock-items'
import { data } from 'config'
import * as routes from 'config/routes'
import useSelectedLocationId from 'hooks/use-selected-location-id'
import PropTypes from 'prop-types'
import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { withRouter } from 'react-router-dom'
import styled from 'styled-components/macro'
import { media } from 'utilities/styled'
import {
  getObjectFromQueryString,
  handleChangeQuery,
  removeQueriesFromQueryString,
  scrollToTop,
} from 'utilities/utils'

const sortOptions = data.rdwStockSortOptions

const StyledStockHeader = styled(StockHeader)`
  margin-bottom: 20px;
`

const StyledResultsCount = styled(Text)`
  grid-area: count;
  font-size: ${({ theme }) => theme.baseFontSize};
`

const InnerContainer = styled.div`
  column-gap: 40px;
  display: grid;
  grid-template-columns: 1fr;
  grid-template-rows: auto auto;

  ${media.desktop`
    grid-template-columns: 200px 1fr;
  `};
`

const StyledFilters = styled(Filters)`
  display: none;

  ${media.desktop`
    display: inline-table;
  `};
`

const MobileFilters = styled(Filters)`
  width: 100%;
`

const RdwStockView = ({
  getRdwStockFilters,
  getRdwStockResults,
  history,
  location,
  rdwItemUpdate,
  rdwStockFilters,
  rdwStockResults,
  updateRdwItem,
}) => {
  const { t } = useTranslation()
  const prevRdwItemUpdate = useRef(rdwItemUpdate)
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false)
  const queryFilters = getObjectFromQueryString(location.search)
  const sortColumn = queryFilters && queryFilters.sort
  const sortDirection = queryFilters && queryFilters.dir
  const page = queryFilters && (queryFilters.page || 1)
  const itemsPerPage = 36
  const selectedSortOption = `sort=${sortColumn}&dir=${sortDirection}`
  const rdwStockResultsData =
    rdwStockResults && rdwStockResults.data && rdwStockResults.data.data
  const rdwStockResultsTotal =
    rdwStockResults && rdwStockResults.data && rdwStockResults.data.total

  const resultsText = Number(rdwStockResultsTotal)
    ? t('numberOfResults', { count: Number(rdwStockResultsTotal) })
    : t('noResults')

  const linkGenerator = (item) => {
    const carId = item.id || item.auto_id

    return carId
      ? `${routes.CAR_FILE}/${carId}`
      : `${routes.CAR_ENTRY}/1${routes.LICENSE_PLATE_SEARCH_BY_LICENSE}?kenteken=${item.kenteken}&mileage=${item.km || item.rdwkm}`
  }

  useEffect(() => {
    const filters = getObjectFromQueryString(location.search)

    getRdwStockFilters({
      ...filters,
      lines: itemsPerPage,
    })
    getRdwStockResults({
      ...filters,
      lines: itemsPerPage,
    })
  }, [getRdwStockFilters, getRdwStockResults, location.search])

  const { vestiging_id: vestigingId } = getObjectFromQueryString(
    location.search,
  )
  const normalizedVestigingId = vestigingId || '0'
  // eslint-disable-next-line no-unused-vars
  const [_, setSelectedLocationId] = useSelectedLocationId(
    normalizedVestigingId,
  )
  useEffect(() => {
    setSelectedLocationId(normalizedVestigingId)
  }, [normalizedVestigingId, setSelectedLocationId])

  useEffect(() => {
    const filters = getObjectFromQueryString(location.search)

    if (
      (!prevRdwItemUpdate.current || !prevRdwItemUpdate.current.loading) &&
      rdwItemUpdate &&
      rdwItemUpdate.loading
    ) {
      getRdwStockResults(filters)
    }

    prevRdwItemUpdate.current = rdwItemUpdate
  }, [getRdwStockResults, location.search, rdwItemUpdate])

  const handleSortOptionChange = (newSortOption) => {
    const newQueryString = removeQueriesFromQueryString(location.search, [
      'sort',
      'dir',
    ]).replace(/&$/, '')

    history.push({
      pathname: routes.RDW_STOCK,
      search: `${newQueryString}&${newSortOption}`,
    })
  }

  const handleToggleMobileFilters = () => {
    setMobileFiltersOpen(!mobileFiltersOpen)
  }

  const filters = rdwStockFilters && rdwStockFilters.data

  const handleResetFilters = () => {
    history.push(routes.RDW_STOCK)
    setMobileFiltersOpen(false)
  }

  const handleFilterUpdate = (filter, value) => {
    handleChangeQuery(location, history, filter, value, filter !== 'page')
  }

  const handleChangePage = (newPage) => {
    handleFilterUpdate('page', newPage)
    scrollToTop()
  }

  return (
    <>
      <SearchResultsOverview
        pageTitle={`${t('rdwStock')}`}
        mainTitle={t('overview')}
        subTitle={t('stock')}
        statusTitle="rdw"
        showSearchbox
        totalResults={() => (
          <StyledResultsCount type="h4" text={resultsText} />
        )}
      >
        <InnerContainer>
          <div />
          {!rdwStockResultsData ? (
            <LoadingIndicator />
          ) : (
            <>
              <StyledStockHeader
                onSortOptionChange={handleSortOptionChange}
                selectedSortOption={selectedSortOption}
                sortOptions={sortOptions.map((option) => ({
                  ...option,
                  label: t(option.label),
                }))}
                toggleMobileFilters={handleToggleMobileFilters}
                total={Number(rdwStockResultsTotal)}
              />
              {filters && (
                <StyledFilters
                  filters={filters}
                  selectedFilters={queryFilters}
                  onUpdate={handleFilterUpdate}
                  onResetFilters={handleResetFilters}
                />
              )}
              <StockItems
                linkGenerator={linkGenerator}
                items={rdwStockResultsData}
                itemsPerPage={itemsPerPage}
                onChangePage={handleChangePage}
                page={Number(page)}
                totalItems={Number(rdwStockResultsTotal)}
                onUpdate={updateRdwItem}
              />
            </>
          )}
        </InnerContainer>
      </SearchResultsOverview>
      {mobileFiltersOpen && (
        <Overlay close={handleToggleMobileFilters} title={t('filters')}>
          <MobileFilters
            filters={filters}
            selectedFilters={queryFilters}
            onUpdate={handleFilterUpdate}
            onResetFilters={handleResetFilters}
          />
        </Overlay>
      )}
    </>
  )
}

RdwStockView.propTypes = {
  getRdwStockFilters: PropTypes.func.isRequired,
  getRdwStockResults: PropTypes.func.isRequired,
  history: PropTypes.object.isRequired,
  location: PropTypes.object.isRequired,
  rdwItemUpdate: PropTypes.object,
  rdwStockFilters: PropTypes.object,
  rdwStockResults: PropTypes.object,
  updateRdwItem: PropTypes.func.isRequired,
}

RdwStockView.defaultProps = {
  rdwItemUpdate: null,
  rdwStockFilters: null,
  rdwStockResults: null,
}

export default withRouter(RdwStockView)
